import * as Logger from '@thoughtspot/logger';
import { getVizRenderingQueueTimeout } from '@thoughtspot/session-service';
import { MapWithArray } from '@thoughtspot/typed';
import { RenderType } from './types';
const SUBSEQUENT_RENDER_DELAY = 50;
const RENDER_TIMEOUT_WAIT = 10000;
class ActiveRenderTask {
    constructor(vizId, type) {
        this.vizId = vizId;
        this.type = type;
        this.start();
    }
    getTimerId() {
        return this.timerId;
    }
    setTimerId(timerId) {
        this.timerId = timerId;
    }
    getVizId() {
        return this.vizId;
    }
    getRenderType() {
        return this.type;
    }
    finish(vizId) {
        if (vizId === this.vizId) {
            this.end();
            return this.getDuration();
        }
        return -1;
    }
    start() {
        this.startTime = Date.now();
    }
    end() {
        this.endTime = Date.now();
    }
    getDuration() {
        const endTime = this.endTime ? this.endTime : Date.now();
        return endTime - this.startTime;
    }
}
const logger = Logger.create('viz-render-queuing-service-v2');
let primaryRenderQueue = new MapWithArray();
let secondaryRenderQueue = new MapWithArray();
let activeRenderTask = null;
function tryRender() {
    if (activeRenderTask) {
        logger.info('Render attempted when active render in progress');
        return;
    }
    let renderQueueEntry;
    if (primaryRenderQueue.getElementCount() > 0) {
        renderQueueEntry = primaryRenderQueue.getArray()[0];
        primaryRenderQueue.removeElementByKey(renderQueueEntry.id);
        activeRenderTask = new ActiveRenderTask(renderQueueEntry.id, RenderType.Primary);
    }
    else if (secondaryRenderQueue.getElementCount() > 0) {
        renderQueueEntry = secondaryRenderQueue.getArray()[0];
        secondaryRenderQueue.removeElementByKey(renderQueueEntry.id);
        activeRenderTask = new ActiveRenderTask(renderQueueEntry.id, RenderType.Secondary);
    }
    else {
        logger.info('No pending vizs to render');
        logger.info(`Used JS Heap size is ${window.performance?.memory?.usedJSHeapSize}`);
    }
    if (activeRenderTask) {
        logger.debug('$$RQ. Starting Render Viz.', `vizId: ${activeRenderTask.getVizId()}`, `renderType: ${RenderType[activeRenderTask.getRenderType()]}`, `PrimaryRenderQLen: ${primaryRenderQueue.getElementCount()}`, `SecondaryRenderQLen: ${secondaryRenderQueue.getElementCount()}`);
        const timerId = renderQueueEntry.task();
        activeRenderTask?.setTimerId(timerId);
    }
}
function clearTimer() {
    const timerId = activeRenderTask?.getTimerId();
    if (timerId) {
        clearTimeout(timerId);
    }
}
function renderComplete(vizId) {
    if (!activeRenderTask) {
        return;
    }
    const renderTime = activeRenderTask.finish(vizId);
    logger.debug('$$RQ. OnRenderComplete.', `vizId: ${vizId}`, `renderType: ${RenderType[activeRenderTask.getRenderType()]}`, `PrimaryRenderQLen: ${primaryRenderQueue.getElementCount()}`, `SecondaryRenderQLen: ${secondaryRenderQueue.getElementCount()}`, `LOAD_TIME: ${renderTime}`);
    if (renderTime !== -1) {
        setTimeout(() => {
            clearTimer();
            activeRenderTask = null;
            tryRender();
        }, SUBSEQUENT_RENDER_DELAY);
    }
}
function renderTimeout(vizId) {
    logger.debug('$$RQ. Render timeout called.', `vizId: ${vizId}`);
    if (vizId === activeRenderTask.getVizId()) {
        logger.debug(`$$RQ. Timing out the viz rendering for vizId: ${vizId}`);
        const secondaryRenderQueueEntry = secondaryRenderQueue.getElementByKey(vizId);
        if (secondaryRenderQueueEntry) {
            secondaryRenderQueue.removeElementByKey(vizId);
            secondaryRenderQueue.addElement(vizId, secondaryRenderQueueEntry);
        }
        renderComplete(vizId);
    }
}
function enqueueRenderRequest(vizId, queue, task) {
    const queueEntry = {
        id: vizId,
        task: () => {
            const timerId = window.setTimeout(() => {
                if (!!activeRenderTask &&
                    activeRenderTask.getVizId() === vizId) {
                    renderTimeout(vizId);
                }
            }, getVizRenderingQueueTimeout() || RENDER_TIMEOUT_WAIT);
            task();
            return timerId;
        },
    };
    queue.addElement(vizId, queueEntry);
    tryRender();
}
function enqueuePrimaryRenderRequest(vizId, task) {
    logger.debug('$$RQ. Render Requested.', `vizId: ${vizId}`, 'renderType: primary');
    enqueueRenderRequest(vizId, primaryRenderQueue, task);
}
function enqueueSecondaryRenderRequest(vizId, task) {
    logger.debug('$$RQ. Render Requested.', `vizId: ${vizId}`, 'renderType: secondary');
    enqueueRenderRequest(vizId, secondaryRenderQueue, task);
}
function renderAborted(vizId) {
    logger.debug('$$RQ. Render aborted.', `vizId: ${vizId}`);
    if (!activeRenderTask) {
        logger.warn('Viz rendering done without queueing');
        return;
    }
    const renderTime = activeRenderTask.finish(vizId);
    logger.debug('$$RQ. OnRenderAborted.', `vizId: ${vizId}`, `renderType: ${RenderType[activeRenderTask.getRenderType()]}`, `PrimaryRenderQLen: ${primaryRenderQueue.getElementCount()}`, `SecondaryRenderQLen: ${secondaryRenderQueue.getElementCount()}`, `LOAD_TIME: ${renderTime}`);
    if (renderTime !== -1) {
        setTimeout(() => {
            clearTimer();
            activeRenderTask = null;
            tryRender();
        }, SUBSEQUENT_RENDER_DELAY);
    }
}
function dequeueRenderRequest(vizId, queue, renderType) {
    if (!!activeRenderTask &&
        activeRenderTask.getVizId() === vizId &&
        activeRenderTask.getRenderType() === renderType) {
        renderAborted(vizId);
    }
    queue.removeElementByKey(vizId);
}
function dequeuePrimaryRenderRequest(vizId) {
    logger.debug('$$RQ. Render Dequeued.', `vizId: ${vizId}`, 'renderType: primary');
    return dequeueRenderRequest(vizId, primaryRenderQueue, RenderType.Primary);
}
function dequeueSecondaryRenderRequest(vizId) {
    logger.debug('$$RQ. Render Dequeued.', `vizId: ${vizId}`, 'renderType: secondary');
    return dequeueRenderRequest(vizId, secondaryRenderQueue, RenderType.Secondary);
}
const clearQueue = () => {
    logger.debug('$$RQ. Render queue cleared.');
    primaryRenderQueue = new MapWithArray();
    secondaryRenderQueue = new MapWithArray();
    clearTimer();
    activeRenderTask = null;
};
export { enqueuePrimaryRenderRequest, enqueueSecondaryRenderRequest, dequeuePrimaryRenderRequest, dequeueSecondaryRenderRequest, renderComplete, clearQueue, };
//# sourceMappingURL=viz-render-queue.js.map